import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FileText, Plus, Search, X } from 'lucide-react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import {
  useAdminQuoteJumpSearch,
  useQuoteApi,
  type QuoteListRow,
} from 'src/api/db/apiQuote'
import { PageContainer } from 'src/shared/components/PageContainer'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { useLocalStorage } from 'src/shared/hooks/useStorage'
import { useDebounce } from 'src/shared/hooks/useDebounce'
import { useToast } from 'src/shared/hooks/use-toast'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { Input } from 'src/shared/components/ui/input'
import { cn } from 'src/shared/components/ui/utils'
import { Combobox } from 'src/shared/components/Combobox'
import { SELECTABLE_QUOTE_STATUS_OPTIONS } from 'src/shared/consts/quoteStatus'
import { buildAdminOrderPath } from 'src/routes/paths'
import { PipelineColumn } from './components/PipelineColumn'

const FilterCheckbox = ({
  id,
  label,
  checked,
  onCheckedChange,
  disabled,
}: {
  id: string
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
}) => (
  <button
    id={id}
    type="button"
    role="checkbox"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onCheckedChange(!checked)}
    className={cn(
      'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
      checked
        ? 'border-foreground bg-foreground text-background'
        : 'border-[hsl(var(--border-subtle))] bg-white/80 text-muted-foreground hover:border-border hover:text-foreground',
      disabled && 'cursor-not-allowed opacity-50'
    )}
  >
    {label}
  </button>
)

const PIPELINE_FILTER_DEFAULTS = {
  unassigned: false,
  showEmpty: false,
  showAnon: false,
  leadMagnet: false,
}

export const AdminOrdersPage = () => {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const { createQuote } = useQuoteApi({ skipListQuery: true })

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)
  const [selectedOrg, setSelectedOrg] = useState('')
  const [orgSearchInput, setOrgSearchInput] = useState('')
  const debouncedOrgSearch = useDebounce(orgSearchInput, 300)
  const [toggles, setToggles] = useLocalStorage(
    'admin-orders-toggles',
    PIPELINE_FILTER_DEFAULTS
  )

  const urlUnassigned = searchParams.get('unassigned') === '1'
  const unassignedOnly = urlUnassigned || toggles.unassigned
  const leadMagnetOnly = !unassignedOnly && Boolean(toggles.leadMagnet)
  const hasItemsOnly = !toggles.showEmpty
  const excludeAnonOrgs = !toggles.showAnon

  useEffect(() => {
    if (!urlUnassigned || toggles.unassigned) return
    setToggles((prev) => ({ ...prev, unassigned: true, showEmpty: true }))
  }, [urlUnassigned, toggles.unassigned, setToggles])

  const setUnassignedOnly = (next: boolean) => {
    setToggles((prev) => ({
      ...prev,
      unassigned: next,
      showEmpty: next,
      leadMagnet: next ? false : prev.leadMagnet,
    }))
    const nextParams = new URLSearchParams(searchParams)
    if (next) nextParams.set('unassigned', '1')
    else nextParams.delete('unassigned')
    setSearchParams(nextParams, { replace: true })
  }

  const setLeadMagnetOnly = (next: boolean) => {
    setToggles((prev) => ({
      ...prev,
      leadMagnet: next,
      unassigned: next ? false : prev.unassigned,
    }))
    if (urlUnassigned && next) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('unassigned')
      setSearchParams(nextParams, { replace: true })
    }
  }

  const { useSearchOrganizations } = useOrgsApi({})
  const { data: searchedOrgs } = useSearchOrganizations({
    search: debouncedOrgSearch,
    limit: 20,
  })

  const { data: jumpSearchResults } = useAdminQuoteJumpSearch(searchInput)

  const jumpResults = jumpSearchResults ?? []
  const showJump = jumpResults.length > 0 && searchInput.trim().length >= 2

  const columnFilters = {
    orgId: unassignedOnly ? undefined : selectedOrg || undefined,
    search: debouncedSearch || undefined,
    excludeAnon: unassignedOnly || leadMagnetOnly ? false : excludeAnonOrgs,
    hasItemsOnly: unassignedOnly || leadMagnetOnly ? false : hasItemsOnly,
    unassignedOnly,
    leadMagnetOnly,
  }

  const orgOptions = useMemo(
    () =>
      (searchedOrgs?.data ?? [])
        .filter((org) => org.name !== 'Anon')
        .map((org) => ({ value: org.id, label: org.name })),
    [searchedOrgs?.data]
  )

  const hasActiveFilters = Boolean(
    searchInput ||
      selectedOrg ||
      !hasItemsOnly ||
      !excludeAnonOrgs ||
      unassignedOnly ||
      leadMagnetOnly
  )

  const handleReset = () => {
    setSearchInput('')
    setSelectedOrg('')
    setOrgSearchInput('')
    setToggles(PIPELINE_FILTER_DEFAULTS)
    if (urlUnassigned) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('unassigned')
      setSearchParams(nextParams, { replace: true })
    }
  }

  const handleCreateUnassigned = async () => {
    try {
      const res = await createQuote.mutateAsync({
        name: t('Untitled request', 'Namnlös förfrågan'),
      })

      if (res?.public_id) {
        setToggles((prev) => ({
          ...prev,
          unassigned: true,
          showEmpty: true,
        }))
        const nextParams = new URLSearchParams(searchParams)
        nextParams.set('unassigned', '1')
        setSearchParams(nextParams, { replace: true })
        navigate(buildAdminOrderPath(res.public_id))
      }
    } catch (error) {
      console.error('Failed to create unassigned proposal:', error)
      toast({
        variant: 'destructive',
        title: t('Something went wrong', 'Något gick fel'),
        description: t(
          'Failed to create proposal',
          'Kunde inte skapa offert'
        ),
      })
    }
  }

  return (
    <PageContainer className="gap-8">
      <Column className="gap-5">
        <Row className="items-start justify-between gap-3">
          <Column className="min-w-0 gap-1">
            <Row className="items-center gap-2">
              <FileText className="h-6 w-6 shrink-0" />
              <h1>{t('Requests', 'Förfrågningar')}</h1>
            </Row>
            <p className="text-sm text-muted-foreground">
              {t(
                'Every request by status — search and filter across the pipeline.',
                'Varje förfrågan per status — sök och filtrera i pipelinen.'
              )}
            </p>
          </Column>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleCreateUnassigned()}
            disabled={createQuote.isPending}
            isLoading={createQuote.isPending}
            className="shrink-0"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            {t('Create unassigned proposal', 'Skapa otilldelad offert')}
          </Button>
        </Row>

        <Row
          className={cn(
            'items-center gap-1.5 rounded-[var(--radius-outer)] border p-1.5',
            'border-[hsl(var(--border-subtle))] bg-[hsl(var(--surface-panel)/0.88)] backdrop-blur-md'
          )}
        >
          <Column className="relative min-w-0 flex-1">
            <Row className="relative items-center">
              <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
              <Input
                variant="ghost"
                fullWidth
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t(
                  'Search name, id, organization, or org id…',
                  'Sök namn, id, organisation eller org-id…'
                )}
                className="h-9 border-0 bg-transparent pl-9 shadow-none"
              />
              {searchInput && (
                <button
                  type="button"
                  className="absolute right-2.5 text-muted-foreground hover:text-foreground"
                  onClick={() => setSearchInput('')}
                  aria-label={t('Clear search', 'Rensa sökning')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </Row>

            {showJump && (
              <Column className="absolute left-0 right-0 top-full z-20 mt-1.5 overflow-hidden rounded-xl border border-[hsl(var(--border-subtle))] bg-background shadow-soft">
                {jumpResults.map((quote: QuoteListRow) => (
                  <button
                    key={quote.id}
                    type="button"
                    className="flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted/60"
                    onClick={() => {
                      navigate(buildAdminOrderPath(quote.public_id ?? ''))
                      setSearchInput('')
                    }}
                  >
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <Column className="min-w-0 gap-0.5">
                      <span className="truncate text-sm">{quote.name}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {[
                          quote.organization?.name ||
                            (!quote.org_id
                              ? t('Unassigned', 'Otilldelad')
                              : null),
                          quote.public_id,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </Column>
                  </button>
                ))}
              </Column>
            )}
          </Column>

          <div className="h-6 w-px shrink-0 bg-[hsl(var(--border-subtle))]" />

          <Row className="shrink-0 items-center gap-3 px-2">
            <FilterCheckbox
              id="pipeline-unassigned"
              label={t('Show unassigned', 'Visa otilldelade')}
              checked={unassignedOnly}
              onCheckedChange={setUnassignedOnly}
            />
            <FilterCheckbox
              id="pipeline-show-empty"
              label={t('Show empty', 'Visa tomma')}
              checked={unassignedOnly || leadMagnetOnly || !hasItemsOnly}
              onCheckedChange={(checked) =>
                setToggles((prev) => ({ ...prev, showEmpty: checked }))
              }
              disabled={unassignedOnly || leadMagnetOnly}
            />
            <FilterCheckbox
              id="pipeline-show-anon"
              label={t('Show anon', 'Visa anonyma')}
              checked={unassignedOnly || !excludeAnonOrgs}
              onCheckedChange={(checked) =>
                setToggles((prev) => ({ ...prev, showAnon: checked }))
              }
              disabled={unassignedOnly}
            />
            <FilterCheckbox
              id="pipeline-show-lead-magnet"
              label={t('Show lead magnet', 'Visa lead magnet')}
              checked={leadMagnetOnly}
              onCheckedChange={setLeadMagnetOnly}
              disabled={unassignedOnly}
            />
          </Row>

          <div className="h-6 w-px shrink-0 bg-[hsl(var(--border-subtle))]" />

          <Column className="w-[200px] shrink-0 mb-0">
            <Combobox
              ariaLabel={t('Organization filter', 'Organisationsfilter')}
              value={unassignedOnly ? '' : selectedOrg}
              onValueChange={setSelectedOrg}
              options={orgOptions}
              onSearchChange={setOrgSearchInput}
              placeholder={t('All organizations', 'Alla organisationer')}
              searchPlaceholder={t(
                'Search organization…',
                'Sök organisation…'
              )}
              emptyMessage={t(
                'No organizations found.',
                'Inga organisationer hittades.'
              )}
              clearOnReselect
              disabled={unassignedOnly}
              buttonVariant="ghost"
              buttonClassName={cn(
                'h-9 justify-between rounded-[var(--radius-tab)] px-3 font-normal hover:bg-black/[0.045]',
                selectedOrg && !unassignedOnly
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            />
          </Column>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="shrink-0"
            >
              <X size={14} strokeWidth={2} className="mr-1 shrink-0" />
              {t('Reset', 'Återställ')}
            </Button>
          )}
        </Row>
      </Column>

      <div className="overflow-x-auto pb-4">
        <Row className="min-w-max gap-4 items-start">
          {SELECTABLE_QUOTE_STATUS_OPTIONS.map((col) => (
            <PipelineColumn
              key={col.value}
              status={col.value}
              label={col.label}
              filters={columnFilters}
            />
          ))}
        </Row>
      </div>
    </PageContainer>
  )
}
